import { aboutFaceColumns, aboutHero } from '~/content/about';
import { ButtonLink } from '~/components/ui';
import { cn } from '~/lib/cn';

/** Staggered entrance matching Webflow `.about-face-card` / hero `faceCardIn`. */
const faceIn = (delayMs: number) =>
  ({
    animation: `aboutFaceIn 800ms cubic-bezier(0.25, 0.1, 0.25, 1) both`,
    animationDelay: `${delayMs}ms`,
  }) as const;

const heroIn = (delayMs: number) =>
  ({
    animation: `aboutFaceIn 500ms ease both`,
    animationDelay: `${delayMs}ms`,
  }) as const;

export function AboutHero() {
  const { title, cta } = aboutHero;

  return (
    <header className="overflow-hidden px-5 pt-[120px] pb-8 md:pb-12 lg:px-20 lg:pt-[148px] lg:pb-20">
      <style>{`
        @keyframes aboutFaceIn {
          from { opacity: 0; transform: translateY(30px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @media (prefers-reduced-motion: reduce) {
          .about-fade { animation: none !important; }
        }
      `}</style>
      <div className="relative mx-auto mb-[53px] w-full max-w-[1280px]">
        <div className="mx-auto flex max-w-[280px] flex-col items-center text-center min-[480px]:max-w-[390px] lg:max-w-[560px]">
          <h1
            className={cn(
              'about-fade mb-6 text-[36px] leading-[1.2] font-semibold tracking-[-0.02em] text-ink sm:text-[44px] lg:text-[64px]',
            )}
            style={heroIn(0)}>
            {title}
          </h1>
          <div className="about-fade" style={heroIn(100)}>
            <ButtonLink href={cta.href}>{cta.label}</ButtonLink>
          </div>
        </div>
      </div>

      <div className="mx-auto flex max-w-[1440px] items-start justify-center">
        <div
          className={cn(
            'mx-auto mt-[-5%] grid min-w-[110%] grid-cols-[1fr_1fr_1.7fr_1fr_1fr] gap-1 min-[480px]:gap-2 lg:gap-4',
          )}>
          {aboutFaceColumns.map((col, colIndex) => (
            <div
              key={colIndex}
              className={cn(
                col.variant === 'sec' && 'pt-[64%]',
                col.variant === 'main' && 'pt-[55%]',
              )}>
              {col.faces.map((face) => {
                const srcSet = face.image.srcSet
                  ?.map(({ src, width }) => `${src} ${width}w`)
                  .join(', ');
                return (
                  <div
                    key={face.name}
                    className={cn(
                      'about-fade mb-1 rounded-[10px] border border-[rgb(120_120_128/0.12)] p-1 min-[480px]:mb-2 lg:mb-4 lg:rounded-xl',
                      face.featured ? 'lg:p-[14px_12px_12px]' : 'lg:p-[10px_8px_8px]',
                    )}
                    style={faceIn(face.delayMs)}>
                    <p
                      className={cn(
                        'mb-2.5 hidden text-ink-secondary min-[768px]:block',
                        face.featured
                          ? 'mb-3 text-[16px] lg:text-[18px]'
                          : 'text-[12px] lg:text-[14px]',
                      )}>
                      {face.name}
                    </p>
                    <img
                      className="h-auto w-full overflow-hidden rounded-md"
                      src={face.image.src}
                      srcSet={srcSet}
                      sizes={face.featured ? '(max-width: 767px) 100vw, 560px' : undefined}
                      width={face.image.width}
                      height={face.image.height}
                      alt={face.image.alt}
                      loading="eager"
                    />
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </div>
    </header>
  );
}
